import { Analytics, AnalyticsEventProperties, AnalyticsEventType, AnalyticsUserProperties } from './analytics'

export enum AuthAnalyticsEventProperty {
   jobTitle = 'Job title',
   authenticationMethod = "Authentication method",
   userType = "User type",
   registrationMethod = "Registration method",
   companySize = "Company size",
   orgCountry = "Org country",
   orgName = "Org name",
   orgWebsite = "Org website",
   logoUploaded = "Logo uploaded"
}

export enum AuthAnalyticsPropertyUtm {
   utmSource = "utm_source",
   utmMedium = "utm_medium",
   utmCampaign = "utm_campaign",
   utmContent = "utm_content",
   utmTerm = "utm_term",
}

export enum AuthAnalyticsEvent {
   accountCreated = 'accountCreated',
   emailVerified = 'Email verified',
   signupStarted = 'Signup started',
   orgSetupCompleted = 'Org setup completed',
   signUpCompleted = 'Sign up completed',
   verificationCodeResent = 'Incorrect code entered'
}

export interface AuthAnalyticsEventOptions extends Record<AnalyticsEventType, AnalyticsEventProperties | undefined> {
   [AuthAnalyticsEvent.accountCreated]: {
      [AuthAnalyticsEventProperty.authenticationMethod]: AnalyticsUserProperties['Authentication method']
      [AuthAnalyticsEventProperty.registrationMethod]: AnalyticsUserProperties['Registration method']
      user: Pick<Partial<AnalyticsUserProperties>, 'Job title'> &
      Pick<AnalyticsUserProperties, 'User type' | 'Authentication method' | 'Registration method'>
   },
   [AuthAnalyticsEvent.emailVerified]: {
      [AuthAnalyticsEventProperty.authenticationMethod]: AnalyticsUserProperties['Authentication method']
   },
   [AuthAnalyticsEvent.signupStarted]: {
      user: Partial<AnalyticsUserProperties>
   },
   [AuthAnalyticsEvent.orgSetupCompleted]: {
      user: Pick<AnalyticsUserProperties, 'Org name' | 'Org country'> &
      Pick<Partial<AnalyticsUserProperties>, 'User type' | 'Org website'>
      "Logo uploaded": boolean
   },
}

export default class AuthAnalytics implements Analytics {
   /** Emits `accountCreated` with the authentication and registration method as event and user properties. */
   static accountCreated = (params: { authenticationMethod: AnalyticsUserProperties['Authentication method'], registrationMethod: AnalyticsUserProperties['Registration method'] }) => {
      return Analytics.emitEvent(AuthAnalyticsEvent.accountCreated, {
         [AuthAnalyticsEventProperty.authenticationMethod]: params.authenticationMethod,
         [AuthAnalyticsEventProperty.registrationMethod]: params.registrationMethod,
         user: {
            [AuthAnalyticsEventProperty.userType]: 'Advisor',
            [AuthAnalyticsEventProperty.authenticationMethod]: params.authenticationMethod,
            [AuthAnalyticsEventProperty.registrationMethod]: params.registrationMethod
         }
      })
   }

   static emailVerified = (params: { authenticationMethod: AnalyticsUserProperties['Authentication method'] }) => {
      return Analytics.emitEvent(AuthAnalyticsEvent.emailVerified, {
         [AuthAnalyticsEventProperty.authenticationMethod]: params.authenticationMethod
      })
   }

   static verificationCodeResent = () => {
      return Analytics.emitEvent(AuthAnalyticsEvent.verificationCodeResent, undefined)
   }

   static signupStarted = (user: Partial<AnalyticsUserProperties>, utmParams: Partial<AuthAnalyticsPropertyUtm>) => {
      return Analytics.emitEvent(AuthAnalyticsEvent.signupStarted, { user, utmParams })
   }

   // sendBeacon, because the caller reloads the page into the app immediately after this fires.
   static signUpCompleted = () => {
      return Analytics.emitEvent(AuthAnalyticsEvent.signUpCompleted, undefined, { immediateAndAsync: true })
   }

   static onOrgSetupCompleted = (params: { [AuthAnalyticsEventProperty.orgCountry]: AnalyticsUserProperties['Org country'], [AuthAnalyticsEventProperty.orgName]: AnalyticsUserProperties['Org name'], [AuthAnalyticsEventProperty.orgWebsite]?: AnalyticsUserProperties['Org website'], [AuthAnalyticsEventProperty.logoUploaded]: boolean }) => {
      return Analytics.emitEvent(AuthAnalyticsEvent.orgSetupCompleted, {
         user: {
            [AuthAnalyticsEventProperty.userType]: 'Advisor',
            [AuthAnalyticsEventProperty.orgCountry]: params[AuthAnalyticsEventProperty.orgCountry],
            [AuthAnalyticsEventProperty.orgName]: params[AuthAnalyticsEventProperty.orgName],
            [AuthAnalyticsEventProperty.orgWebsite]: params[AuthAnalyticsEventProperty.orgWebsite]
         },
         [AuthAnalyticsEventProperty.logoUploaded]: params[AuthAnalyticsEventProperty.logoUploaded]
      }, { immediateAndAsync: true })
   }
}
